import { FC } from 'react'
import { useTranslation } from 'react-i18next'

import { CardShell, CopyButton, DRAFT_META } from '../errand-actions/errand-action-card'
import MarkdownRenderer from '../MarkdownRenderer'

// Input mirrors the backend tool schema (create-draft.ts).
type CreateDraftInput = {
  content?: string
  title?: string
}

const chatCardClassName = 'mb-8 w-full bg-layer-1 border-2 border-brand-funkia'

const ChatMessageCreateDraft: FC<{ input?: CreateDraftInput }> = ({ input }) => {
  const { t } = useTranslation()
  if (!input?.content) return null

  // The tool only produces text drafts, so this renders the live tool call rather than a
  // stored row and has no type to branch on.
  const meta = DRAFT_META.text

  return (
    <CardShell
      className={chatCardClassName}
      footer={<CopyButton content={input.content} />}
      icon={meta.icon}
      label={t(meta.labelKey)}
    >
      {input.title && <p className="mb-2 font-medium">{input.title}</p>}
      <MarkdownRenderer className="max-w-prose">{input.content}</MarkdownRenderer>
    </CardShell>
  )
}

/**
 * Renders an AI-generated errand action inline in the chat from its tool-call part. Returns null
 * for tool parts we don't render (or before output is available), so the caller can fall through.
 */
export const ChatMessageAction: FC<{ part: { output?: unknown; type: string } }> = ({ part }) => {
  // The tool returns its finalized payload from `execute`, so read `output` uniformly. Until the
  // tool finishes (output available), render nothing rather than partial/unfiltered input.
  if (part.type === 'tool-createDraft') {
    return <ChatMessageCreateDraft input={part.output as CreateDraftInput | undefined} />
  }

  return null
}
